import { useLingui, Trans } from '@lingui/react/macro';
import { Badge, Button, toast } from '@utima/ui';
import { Copy, Pencil, Play, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { trpc, type RouterOutputs } from '@/services/trpc';

import { HttpToolDialog } from './HttpToolDialog';
import { useHttpToolMutation } from '../hooks/useHttpToolMutation';

type HttpToolRow = RouterOutputs['httpTools']['findAll'][number];

export function HttpToolsTab() {
  const { t } = useLingui();
  const { data: httpTools, isLoading } = trpc.httpTools.findAll.useQuery();
  const { create, remove, testConnection } = useHttpToolMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<HttpToolRow | undefined>();

  const openCreate = () => {
    setEditing(undefined);
    setDialogOpen(true);
  };

  const openEdit = (httpTool: HttpToolRow) => {
    setEditing(httpTool);
    setDialogOpen(true);
  };

  const handleTest = async (httpTool: HttpToolRow) => {
    try {
      const result = await testConnection.mutateAsync({ id: httpTool.id });

      if ('httpStatus' in result) {
        if (result.status === 'ok') {
          toast.success(t`Request succeeded (HTTP ${result.httpStatus}).`);
        } else {
          toast.warning(t`Server responded with HTTP ${result.httpStatus}.`);
        }
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t`Test failed.`);
    }
  };

  const handleDuplicate = async (httpTool: HttpToolRow) => {
    try {
      await create.mutateAsync({
        name: `${httpTool.name}_copy`.slice(0, 60),
        description: httpTool.description,
        url: httpTool.url,
        method: httpTool.method,
        parameters: httpTool.parameters,
        bodyExample: httpTool.bodyExample,
        bodyInstructions: httpTool.bodyInstructions,
        responseMapping: httpTool.responseMapping,
        timeoutMs: httpTool.timeoutMs,
        availableToAllChats: httpTool.availableToAllChats,
        chatIds: httpTool.chatIds ?? [],
        isActive: false,
      });
      toast.success(t`Tool duplicated (headers not copied).`);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t`Failed to duplicate tool.`,
      );
    }
  };

  const handleDelete = async (httpTool: HttpToolRow) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t`Delete HTTP tool "${httpTool.name}"?`)) {
      return;
    }

    try {
      await remove.mutateAsync({ id: httpTool.id });
      toast.success(t`HTTP tool deleted.`);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t`Failed to delete HTTP tool.`,
      );
    }
  };

  const items = httpTools ?? [];

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <span className='text-sm font-medium'>
          <Trans>Your HTTP tools</Trans>
        </span>
        <Button size='sm' onClick={openCreate}>
          <Plus className='size-4' />
          <Trans>Add HTTP tool</Trans>
        </Button>
      </div>

      {isLoading && (
        <p className='text-sm text-muted-fg'>
          <Trans>Loading…</Trans>
        </p>
      )}

      {!isLoading && items.length === 0 && (
        <div className='rounded-radius border border-dashed border-border p-8 text-center text-sm text-muted-fg'>
          <Trans>
            No HTTP tools yet. Define an API call to give your agents a custom
            tool.
          </Trans>
        </div>
      )}

      {items.length > 0 && (
        <div className='overflow-hidden rounded-radius border border-border'>
          <table className='w-full text-sm'>
            <thead className='bg-muted/40 text-left text-muted-fg'>
              <tr>
                <th className='p-3 font-medium'>
                  <Trans>Name</Trans>
                </th>
                <th className='p-3 font-medium'>
                  <Trans>Method</Trans>
                </th>
                <th className='p-3 font-medium'>
                  <Trans>URL</Trans>
                </th>
                <th className='p-3 font-medium'>
                  <Trans>Scope</Trans>
                </th>
                <th className='p-3 text-right font-medium'>
                  <Trans>Actions</Trans>
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map(httpTool => (
                <tr key={httpTool.id} className='border-t border-border'>
                  <td className='p-3'>
                    <div className='font-medium'>{httpTool.name}</div>
                    {!httpTool.isActive && (
                      <Badge variant='muted' size='xs'>
                        <Trans>Inactive</Trans>
                      </Badge>
                    )}
                  </td>
                  <td className='p-3'>
                    <Badge variant='muted' size='xs'>
                      {httpTool.method}
                    </Badge>
                  </td>
                  <td className='max-w-72 truncate p-3 text-muted-fg'>
                    {httpTool.url}
                  </td>
                  <td className='p-3'>
                    {httpTool.availableToAllChats ? (
                      <Trans>All chats</Trans>
                    ) : (
                      <Trans>{httpTool.chatIds?.length ?? 0} chats</Trans>
                    )}
                  </td>
                  <td className='p-3'>
                    <div className='flex justify-end gap-1'>
                      <Button
                        size='sm'
                        variant='ghost'
                        title={t`Test`}
                        loading={
                          testConnection.isPending &&
                          testConnection.variables?.id === httpTool.id
                        }
                        onClick={() => handleTest(httpTool)}
                      >
                        <Play className='size-4' />
                      </Button>
                      <Button
                        size='sm'
                        variant='ghost'
                        title={t`Duplicate`}
                        onClick={() => handleDuplicate(httpTool)}
                      >
                        <Copy className='size-4' />
                      </Button>
                      <Button
                        size='sm'
                        variant='ghost'
                        title={t`Edit`}
                        onClick={() => openEdit(httpTool)}
                      >
                        <Pencil className='size-4' />
                      </Button>
                      <Button
                        size='sm'
                        variant='ghost'
                        title={t`Delete`}
                        onClick={() => handleDelete(httpTool)}
                      >
                        <Trash2 className='size-4 text-danger' />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <HttpToolDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        httpTool={editing}
      />
    </div>
  );
}
